import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../utilities/axiosInstance";

const initialState = {
  data: [],
  itemsCount: 0,
  pages: 0,
  isLoading: false,
  error: null,

  filter: {},
  page: 1,
  query: "",
};

export const getPropertyVerifications = createAsyncThunk(
  "propertyVerification/getPropertyVerifications",
  async ({ page, query, filter = {}, owner, isVerified }, thunkApi) => {
    try {
      const requestParams = {
        page,
      };

      if (query) requestParams.query = query;
      if (owner) requestParams.owner = owner;
      if (isVerified !== undefined) requestParams.isVerified = isVerified;

      // Only include non-empty filter properties
      Object.keys(filter).forEach(key => {
        if (filter[key] !== undefined && filter[key] !== null && filter[key] !== '') {
          requestParams[key] = filter[key];
        }
      });

      const response = await axiosInstance.get(`/verifications`, {
        params: requestParams,
      });
      return response.data;
    } catch (error) {
      return thunkApi.rejectWithValue(
        error.response?.data?.message ||
          "Failed to fetch property verifications"
      );
    }
  }
);

export const updatePropertyVerification = createAsyncThunk(
  "propertyVerification/updatePropertyVerification",
  async ({ id, data }, thunkApi) => {
    try {
      const response = await axiosInstance.put(`/verifications/${id}`, data);
      return response.data;
    } catch (error) {
      return thunkApi.rejectWithValue(
        error.response?.data?.message ||
          "Failed to update property verification"
      );
    }
  }
);

export const createPropertyVerification = createAsyncThunk(
  "propertyVerification/createPropertyVerification",
  async (data, thunkApi) => {
    try {
      const response = await axiosInstance.post(`/verifications`, data);
      return response.data;
    } catch (error) {
      return thunkApi.rejectWithValue(
        error.response?.data?.message ||
          "Failed to create property verification"
      );
    }
  }
);

export const confirmPropertyVerification = createAsyncThunk(
  "propertyVerification/confirmPropertyVerification",
  async ({ id, country }, thunkApi) => {
    try {
      const response = await axiosInstance.post(
        `/verifications/confirm/${id}`,
        {},
        {
          params: { country },
        }
      );
      return response.data;
    } catch (error) {
      return thunkApi.rejectWithValue(
        error.response?.data?.message ||
          "Failed to confirm property verification"
      );
    }
  }
);

export const getOtherOwnersVerifications = createAsyncThunk(
  "propertyVerification/getOtherOwnersVerifications",
  async ({ page, query = "", filter = {}, isVerified }, thunkApi) => {
    try {
      const params = { page };

      if (query) params.query = query;
      if (isVerified !== undefined) params.isVerified = isVerified;

      // Only include non-empty filter properties
      Object.keys(filter).forEach(key => {
        if (filter[key] !== undefined && filter[key] !== null && filter[key] !== '') {
          params[key] = filter[key];
        }
      });

      const response = await axiosInstance.get(`/verifications/list`, {
        params,
      });
      return response.data;
    } catch (error) {
      return thunkApi.rejectWithValue(
        error.response?.data?.message ||
          "Failed to fetch other owners verifications"
      );
    }
  }
);
export const ignorePropertyVerification = createAsyncThunk(
  "propertyVerification/ignorePropertyVerification",
  async ({ _id }, thunkApi) => {
    try {
      const response = await axiosInstance.post(
        `/verifications/${_id}/ignore`,
        {}
      );
      return response.data;
    } catch (error) {
      return thunkApi.rejectWithValue(
        error.response?.data?.message ||
          "Failed to confirm property verification"
      );
    }
  }
);

export const getVerificationTours = createAsyncThunk(
  "propertyVerification/getVerificationTours",
  async ({ page, size = 10, verifiedBy }, thunkApi) => {
    try {
      const params = { page, size };

      if (verifiedBy) params.verifiedBy = verifiedBy;

      const response = await axiosInstance.get(`/verifications`, {
        params,
      });
      return response.data;
    } catch (error) {
      return thunkApi.rejectWithValue(
        error.response?.data?.message ||
          "Failed to fetch verification tours"
      );
    }
  }
);
const propertyVerificationSlice = createSlice({
  name: "propertyVerification",
  initialState,
  reducers: {
    resetState: () => initialState,
    resetFiltersAndPagination: (state) => {
      state.filter = {};
      state.page = 1;
      state.query = "";
    },
    clearError: (state) => {
      state.error = null;
    },
    setFilter: (state, action) => {
      state.filter = action.payload;
    },
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setQuery: (state, action) => {
      state.query = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPropertyVerifications.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.data = [];
        state.itemsCount = 0;
        state.pages = 0;
      })
      .addCase(getPropertyVerifications.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload.data?.data || [];
        state.itemsCount = action.payload.data?.itemsCount || 0;
        state.pages = action.payload.data?.pages || 0;
      })
      .addCase(getPropertyVerifications.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updatePropertyVerification.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updatePropertyVerification.fulfilled, (state, action) => {
        state.isLoading = false;
      })
      .addCase(updatePropertyVerification.rejected, (state, action) => {
        state.isLoading = false;
        // state.error = action.payload;
      })
      .addCase(createPropertyVerification.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createPropertyVerification.fulfilled, (state, action) => {
        state.isLoading = false;
        // Add the new item to the data array
        state.data.unshift(action.payload.data);
        state.itemsCount += 1;
      })
      .addCase(createPropertyVerification.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(confirmPropertyVerification.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(confirmPropertyVerification.fulfilled, (state, action) => {
        state.isLoading = false;
      })
      .addCase(confirmPropertyVerification.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(getOtherOwnersVerifications.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.data = [];
        state.itemsCount = 0;
        state.pages = 0;
      })
      .addCase(getOtherOwnersVerifications.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload.data?.data || [];
        state.itemsCount = action.payload.data?.itemsCount || 0;
        state.pages = action.payload.data?.pages || 0;
      })
      .addCase(getOtherOwnersVerifications.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      .addCase(ignorePropertyVerification.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(ignorePropertyVerification.fulfilled, (state, action) => {
        state.isLoading = false;
      })
      .addCase(ignorePropertyVerification.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(getVerificationTours.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.data = [];
        state.itemsCount = 0;
        state.pages = 0;
      })
      .addCase(getVerificationTours.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload.data?.data || [];
        state.itemsCount = action.payload.data?.itemsCount || 0;
        state.pages = action.payload.data?.pages || 0;
      })
      .addCase(getVerificationTours.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, setFilter, setPage, setQuery, resetState, resetFiltersAndPagination } =
  propertyVerificationSlice.actions;
export default propertyVerificationSlice.reducer;
